import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MenuIcon, SearchIcon, ShoppingBagIcon, UserIcon, XIcon } from "lucide-react";
import { Logo } from "./Logo";
import { useCart } from "../contexts/CartContext";
import { store } from "../data/store";
const navItems = [{
  label: "Shop all",
  to: "/shop"
}, {
  label: "Honey & Choco",
  to: "/shop?collection=royal-honey"
}, {
  label: "Seven Sins",
  to: "/shop?collection=seven-sins"
}, {
  label: "Devils Candy",
  to: "/shop?collection=devils-candy"
}, {
  label: "For her",
  to: "/shop?for=her"
}, {
  label: "For him",
  to: "/shop?for=him"
}];
const messages = [`Free discreet shipping on orders over €${store.freeShippingThreshold}`, "Every order ships in plain, unbranded packaging", "Shipped fast from the Netherlands"];
const ease = [0.23, 1, 0.32, 1] as const;
export function Header() {
  const {
    count,
    openCart
  } = useCart();
  const {
    pathname,
    search
  } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [msg, setMsg] = useState(0);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    const t = setInterval(() => setMsg(m => (m + 1) % messages.length), 4500);
    return () => clearInterval(t);
  }, []);
  useEffect(() => setMenuOpen(false), [pathname, search]);
  const solid = pathname !== "/" || scrolled || menuOpen;
  return <header className="fixed inset-x-0 top-0 z-40" data-id="element-124" data-mp="30mzgfttakhtg">
      <div className="relative h-8 overflow-hidden bg-brand text-night" data-id="element-125" data-mp="30kd8vu6ei2b8">
        <AnimatePresence mode="wait" initial={false} data-id="element-126" data-mp="1x7mg2bo0gp7">
          <motion.p key={msg} initial={{
          y: 14,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: -14,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease
        }} className="absolute inset-0 flex items-center justify-center px-6 text-xs font-semibold" data-id="element-127" data-mp="3tbdeg9kdre50">
            {messages[msg]}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className={`border-b transition-[background-color,border-color] duration-200 ease-brand ${solid ? "border-line bg-night/85 backdrop-blur-xl" : "border-transparent bg-transparent"}`} data-id="element-128" data-mp="1mm31lppb7bcy">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-6 lg:h-[72px]" data-id="element-129" data-mp="1cq8eaha54s3u">
          <div className="flex items-center gap-2" data-id="element-130" data-mp="yqqjnla5ykmk">
            <button type="button" onClick={() => setMenuOpen(v => !v)} className="-ml-2 rounded-full p-2 transition-colors hover:bg-white/10 lg:hidden" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} data-id="element-131" data-mp="13nh049pvri8i">
              {menuOpen ? <XIcon className="h-5 w-5" data-id="element-132" data-mp="mfg1wh7m1fcr" /> : <MenuIcon className="h-5 w-5" data-id="element-133" data-mp="mck54w7i0wpw" />}
            </button>
            <Link to="/" aria-label="Rise Upp home" data-id="element-134" data-mp="hgwdma5jnrh8">
              <Logo data-id="element-135" data-mp="w4hjucxpu6td" />
            </Link>
          </div>

          <nav aria-label="Main" className="hidden lg:block" data-id="element-136" data-mp="w60vtrcc9vpc">
            <ul className="flex items-center gap-7" data-id="element-137" data-mp="rd7wjizn3fkn">
              {navItems.map(item => <li key={item.label} data-id="element-138" data-mp="154mmk9rysh17">
                  <Link to={item.to} className="relative whitespace-nowrap py-1 text-sm font-medium text-white/80 transition-colors hover:text-white after:absolute after:-bottom-0.5 after:left-0 after:h-[2px] after:w-full after:origin-left after:scale-x-0 after:rounded-full after:bg-brand after:transition-transform after:duration-200 after:ease-brand hover:after:scale-x-100" data-id="element-139" data-mp="153l1dch8ie5r">
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>

          <div className="flex items-center gap-1" data-id="element-140" data-mp="22zgli461wmng">
            <Link to="/shop" className="hidden rounded-full p-2.5 transition-colors hover:bg-white/10 sm:inline-flex" aria-label="Search products" data-id="element-141" data-mp="230ro40nrfocz">
              <SearchIcon className="h-5 w-5" data-id="element-142" data-mp="2cv0fum4yvda3" />
            </Link>
            <button type="button" className="hidden rounded-full p-2.5 transition-colors hover:bg-white/10 sm:inline-flex" aria-label="Account" data-id="element-143" data-mp="2gx59vlzbubx9">
              <UserIcon className="h-5 w-5" data-id="element-144" data-mp="1p81zrpkre7cp" />
            </button>
            <button type="button" onClick={openCart} className="relative flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-night transition-colors hover:bg-brand" aria-label={`Open cart, ${count} items`} data-id="element-145" data-mp="1t8c6j4gdl1sm">
              <ShoppingBagIcon className="h-4 w-4" aria-hidden="true" data-id="element-146" data-mp="15ihw0tjj3pbj" />
              <span className="hidden sm:inline" data-id="element-147" data-mp="xzs3edoxs8yx">Cart</span>
              <motion.span key={count} initial={{
              scale: 0.6
            }} animate={{
              scale: 1
            }} transition={{
              type: "spring",
              stiffness: 600,
              damping: 18
            }} className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-night px-1 text-[11px] text-white" data-id="element-148" data-mp="xz8n11expsn0">
                {count}
              </motion.span>
            </button>
          </div>
        </div>

        <AnimatePresence data-id="element-149" data-mp="a9o2wf71opc5">
          {menuOpen && <motion.nav aria-label="Mobile" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: "auto",
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease
        }} className="overflow-hidden border-t border-line lg:hidden" data-id="element-150" data-mp="a8v0fqf4clzt">
              <ul className="space-y-1 px-6 py-4" data-id="element-151" data-mp="3ng6cuewm97uz">
                {navItems.map((item, i) => <motion.li key={item.label} initial={{
              opacity: 0,
              x: -12
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.2,
              delay: i * 0.04,
              ease
            }} data-id="element-152" data-mp="3dskfnpqymmsv" data-mpi={i}>
                    <Link to={item.to} className="block rounded-lg px-3 py-3 font-display text-xl font-semibold hover:bg-raised" data-id="element-153" data-mp="33xah5bzrsxju">
                      {item.label}
                    </Link>
                  </motion.li>)}
              </ul>
            </motion.nav>}
        </AnimatePresence>
      </div>
    </header>;
}